import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';

/**
 * Install prompt banner for PWA (Android/Chrome). Mostra un suggerimento morbido
 * dopo 20s di sessione se il browser emette `beforeinstallprompt` e l'utente non
 * ha mai dismesso il prompt (localStorage flag).
 */
const DISMISS_KEY = 'agn_pwa_dismissed_v1';
const SHOW_AFTER_MS = 20_000;

export default function PwaInstallPrompt() {
  const [evt, setEvt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY)) return;
    // Hide on iOS: beforeinstallprompt is Chromium-only. iOS users will use the
    // Share → "Aggiungi alla Home" flow (no custom UI possible by browser policy).
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setEvt(e);
      setTimeout(() => setShow(true), SHOW_AFTER_MS);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, '1'); };
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!evt) return;
    evt.prompt();
    const choice = await evt.userChoice;
    if (choice.outcome === 'accepted') {
      localStorage.setItem(DISMISS_KEY, '1');
    }
    setShow(false);
    setEvt(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  };

  if (!show) return null;
  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-20 sm:bottom-6 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-sm z-[70] rounded-2xl border border-border bg-card shadow-2xl p-4 flex items-start gap-3"
    >
      <div className="shrink-0 w-10 h-10 rounded-xl bg-chartreuse/15 flex items-center justify-center">
        <Download size={18} className="text-chartreuse-foreground" style={{ color: 'var(--chartreuse-foreground, #2a6207)' }}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Installa AgriNexus</div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
          Aggiungi l'app alla home per cercare terreni più velocemente, anche offline.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" onClick={install} data-testid="pwa-install-btn" className="rounded-full h-8">
            Installa
          </Button>
          <Button size="sm" variant="ghost" onClick={dismiss} className="h-8 text-muted-foreground">
            Non ora
          </Button>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="shrink-0 text-muted-foreground hover:text-foreground"
        aria-label="Chiudi"
      >
        <X size={16}/>
      </button>
    </div>
  );
}
